import { Check, Info, PlayCircle } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'

import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import API from '../../../../../api/api'
import MediaUpload from '../../../../MediaUpload/MediaUpload'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Filter from '../../../../Filter/Filter'
import OptionsPills from '../../../../OptionsPills/OptionsPills'
import { AmenityImageUpload } from '../components/amenity-image-upload'
import VideoPreviewModal from '../../../../VideoPreviewModal/VideoPreviewModal'
import {
  MAX_SQUARE_FEET,
  MAX_CAPACITY,
  ONE_HUNDRED_MB,
  getAmenityCategories,
  getBenefits,
  AMENITY_OVERVIEW_FILE_LABEL,
  AMENITY_AGREEMENT_FILE_LABEL,
  onChangeNumberField,
  initialValues,
  getAmenityTypeOptions,
  AMENITY_TYPES,
  saltoSpaceDropdownStyles,
} from '../helpers'
import { selectActiveBuilding } from '../../../../../reducers/buildingSelectorReducer'

const AmenityDetails = ({
  values,
  errors = {},
  isEdit = false,
  setFieldValue = () => {},
  handleChange = () => {},
  setIsMultiDayBooking = () => {},
  resetForm = () => {},
}) => {
  const { t } = useTranslation(['reservations'])
  const { displaySnackbar } = useSnackbar()
  const activeBuilding = useSelector(selectActiveBuilding)
  const AMENITY_CATEGORIES = getAmenityCategories(t)
  const BENEFITS = getBenefits(t)
  const AMENITY_TYPE_OPTIONS = getAmenityTypeOptions(t)

  const [saltoSpaceDoors, setSaltoSpaceDoors] = useState([])
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false)

  const handleRegulationUpdate = (file) => {
    if (file) {
      setFieldValue('regulation', file)
    }
  }

  const handleAgreementUpdate = (file) => {
    if (file) {
      setFieldValue('agreement', file)
    }
  }

  const handleAmenityTypeChange = (value) => {
    setFieldValue('multiDayBooking', value === AMENITY_TYPES.MULTIDAY)
    setIsMultiDayBooking(value === AMENITY_TYPES.MULTIDAY)

    const updatedValues = {
      ...initialValues,
      multiDayBooking: value === AMENITY_TYPES.MULTIDAY,
      name: values.name,
      image: values.image,
      description: values.description,
      spaceType: values.spaceType,
      benefits: values.benefits,
      saltoSpaceDoors: values.saltoSpaceDoors,
      squareFeet: values.squareFeet,
      maxGuests: values.maxGuests,
      regulation: values.regulation,
      agreement: values.agreement,
      isSignatureRequired: values.isSignatureRequired,
      hidden: values.hidden,
      saltoSpaceAccessAvailable: values.saltoSpaceAccessAvailable,
    }
    resetForm(updatedValues)
  }

  const handleRemoveBenefit = (benefitToRemove) => {
    const updatedBenefits = values.benefits.filter((benefit) => benefit.id !== benefitToRemove.id)
    setFieldValue('benefits', updatedBenefits)
  }

  const handlePlayVideo = () => {
    setIsVideoModalVisible(true)
  }

  const handleCloseVideo = () => {
    setIsVideoModalVisible(false)
  }

  const fetchSaltoSpaceAccessData = async () => {
    try {
      const { data } = await API.getBuildingSaltoSpaceAccessPoints(activeBuilding.id)
      setSaltoSpaceDoors(data.doors)
    } catch {
      displaySnackbar(t('reservations:singleReservationAmenityForm.genericError'), true)
    }
  }

  const isAmenitiesAcrAvailable =
    activeBuilding?.amenityReservations && activeBuilding?.saltoSpaceAvailable
      ? activeBuilding?.amenitiesAcrAvailable
      : false

  useEffect(() => {
    if (isAmenitiesAcrAvailable) {
      fetchSaltoSpaceAccessData()
    }
  }, [isAmenitiesAcrAvailable])

  return (
    <>
      <div className="form-step">
        <div className="form-step__content">
          <div className="form-step__section">
            <div className="form-step__row">
              <AmenityImageUpload
                image={values.image}
                onChange={(image) => setFieldValue('image', image)}
                error={errors.image}
              />
            </div>
            <div className="form-step__row">
              <TextField
                name="name"
                label={t('reservations:amenityForms.details.amenityName')}
                value={values.name || ''}
                onChange={handleChange}
                isError={!!errors.name}
                maxLength={180}
              />
            </div>
            <div className="form-step__row">
              <div className="form-step__column">
                <TextAreaField
                  name="description"
                  label={t('reservations:amenityForms.details.description')}
                  onChange={handleChange}
                  value={values.description || ''}
                  maxLength={1000}
                  isError={!!errors.description}
                />
                <span className="input-desc-label">
                  {t('reservations:amenityForms.details.maxInputLength1000')}
                </span>
              </div>
            </div>
            <div className="form-step__row">
              <SelectField
                name="spaceType"
                label={t('reservations:amenityForms.details.amenityCategory')}
                value={values.spaceType || ''}
                options={AMENITY_CATEGORIES}
                onChange={handleChange}
                isError={!!errors?.spaceType}
              />
            </div>
            <div className="form-step__row">
              <div className="form-step__column">
                <Filter
                  name="benefits"
                  label={t('reservations:amenityForms.details.includedBenefits')}
                  config={{ multiple: true, hasSearch: true }}
                  value={values.benefits || []}
                  placeholder={
                    values?.benefits?.length > 0
                      ? values.benefits.map((benefit) => benefit.text).join(', ')
                      : t('reservations:amenityForms.details.selectOptionsPlaceholder')
                  }
                  options={BENEFITS}
                  onChange={handleChange}
                />
                <OptionsPills
                  items={
                    BENEFITS.filter((benefit) =>
                      values.benefits.some((b) => b.id === benefit.id),
                    ) || []
                  }
                  onRemove={handleRemoveBenefit}
                />
              </div>
            </div>
            <div className="form-step__row">
              <TextField
                name="squareFeet"
                label={t('reservations:amenityForms.details.amenitySquareFootage')}
                value={values?.squareFeet || ''}
                onChange={(e) => onChangeNumberField(e, handleChange)}
                min={0}
                max={MAX_SQUARE_FEET}
                type="number"
              />
              <span className="input-suffix input-suffix--number">
                {t('reservations:amenityForms.details.sqFt')}
              </span>
            </div>
            <div className="form-step__row">
              <TextField
                name="maxGuests"
                label={t('reservations:amenityForms.details.maxGuests')}
                value={values?.maxGuests || ''}
                onChange={(e) => onChangeNumberField(e, handleChange)}
                type="number"
                onlyIntegers={true}
                min={0}
                max={MAX_CAPACITY}
              />
            </div>
            <div className="form-step__row">
              <div className="form-step__setting-box">
                <div>
                  <div>
                    <div className="form-step__label">
                      {t('reservations:amenityForms.details.amenityOverview')}
                      <Info size={16} data-tip data-for="amenityOverview" />
                      <ReactTooltip
                        className="tooltip"
                        multiline={true}
                        id="amenityOverview"
                        place="top"
                        effect="solid"
                      >
                        {t('reservations:amenityForms.details.amenityOverviewTooltip')}
                      </ReactTooltip>
                    </div>
                  </div>
                  <div>
                    <div className="form-step__upload-container">
                      <MediaUpload
                        icon="arrowDownload"
                        mediaTypes={['pdf']}
                        onUpdate={handleRegulationUpdate}
                        onRemove={() => setFieldValue('regulation', null)}
                        downloadAssetPath="chitown-building"
                        persistedFileLabel={AMENITY_OVERVIEW_FILE_LABEL}
                        files={values?.regulation ? [values.regulation] : []}
                        maxSize={ONE_HUNDRED_MB}
                        variant="lightgray"
                        buttonText={t('reservations:singleReservationAmenityForm.uploadOverview')}
                      />
                      <span className="form-step__upload-desc">
                        {t('reservations:amenityForms.details.acceptedPdfFiles')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="form-step__row">
              <div className="form-step__setting-box">
                <div>
                  <div>
                    <div className="form-step__label">
                      {t('reservations:amenityForms.details.amenityAgreement')}
                      <Info size={16} data-tip data-for="amenityAgreement" />
                      <ReactTooltip
                        className="tooltip"
                        multiline={true}
                        id="amenityAgreement"
                        place="top"
                        effect="solid"
                      >
                        {t('reservations:amenityForms.details.amenityAgreementTooltip')}
                      </ReactTooltip>
                    </div>
                    <button type="button" className="button button-blue" onClick={handlePlayVideo}>
                      <PlayCircle size={16} />
                      {t('reservations:amenityForms.details.watchTutorial')}
                    </button>
                  </div>
                  <div>
                    <div className="form-step__upload-container">
                      <MediaUpload
                        icon="arrowDownload"
                        mediaTypes={['pdf']}
                        onUpdate={handleAgreementUpdate}
                        onRemove={() => setFieldValue('agreement', null)}
                        downloadAssetPath="chitown-building"
                        persistedFileLabel={AMENITY_AGREEMENT_FILE_LABEL}
                        files={values.agreement ? [values.agreement] : []}
                        maxSize={ONE_HUNDRED_MB}
                        variant="lightgray"
                        buttonText={t('reservations:singleReservationAmenityForm.uploadAgreement')}
                      />
                      <span className="form-step__upload-desc">
                        {t('reservations:amenityForms.details.acceptedPdfFiles')}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <div className="form-step__label">
                      {t('reservations:amenityForms.details.mustSignBeforeBooking')}
                      <Info size={16} data-tip data-for="mustSignBeforeBooking" />
                      <ReactTooltip
                        className="tooltip"
                        multiline={true}
                        id="mustSignBeforeBooking"
                        place="top"
                        effect="solid"
                      >
                        {t('reservations:amenityForms.details.mustSignBeforeBookingTooltip')}
                      </ReactTooltip>
                    </div>
                  </div>
                  <div>
                    <ToggleField
                      name="isSignatureRequired"
                      label={
                        values.isSignatureRequired
                          ? t('reservations:amenityForms.yes')
                          : t('reservations:amenityForms.no')
                      }
                      value={values.isSignatureRequired || false}
                      onChange={handleChange}
                      labelOnRight
                      disabled={!values.agreement}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-step__section">
            <div className="form-step__row form-step__row--between">
              <div>
                <div className="form-step__label">
                  {t('reservations:amenityForms.details.amenityType')}
                  <Info size={16} data-tip data-for="amenityType" />
                  <ReactTooltip
                    className="tooltip"
                    multiline={true}
                    id="amenityType"
                    place="top"
                    effect="solid"
                  >
                    {t('reservations:amenityForms.details.amenityTypeTooltipIntro')} <br />
                    <strong>{t('reservations:amenityForms.types.hourly')}</strong>{' '}
                    {t('reservations:amenityForms.details.amenityTypeTooltipHourly')}
                    <br />
                    <strong>{t('reservations:amenityForms.types.multiday')}</strong>{' '}
                    {t('reservations:amenityForms.details.amenityTypeTooltipMultiday')}
                  </ReactTooltip>
                </div>
              </div>
              <div>
                <div className={`form-step__switch ${isEdit ? 'form-step__switch--disabled' : ''}`}>
                  {AMENITY_TYPE_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`form-step__switch-segment ${
                        (values.multiDayBooking && option.value === AMENITY_TYPES.MULTIDAY) ||
                        (!values.multiDayBooking && option.value === AMENITY_TYPES.HOURLY)
                          ? 'form-step__switch-segment--active'
                          : ''
                      }`}
                      onClick={(e) => {
                        e.preventDefault()
                        handleAmenityTypeChange(option.value)
                      }}
                    >
                      {(values.multiDayBooking && option.value === AMENITY_TYPES.MULTIDAY) ||
                      (!values.multiDayBooking && option.value === AMENITY_TYPES.HOURLY) ? (
                        <Check size={16} weight="bold" />
                      ) : null}
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-step__divider" />
            <div className="form-step__row form-step__row--between">
              <div>
                <div className="form-step__label">
                  {t('reservations:amenityForms.details.allowResidentsToBook')}
                  <Info size={16} data-tip data-for="allowResidentsToBook" />
                  <ReactTooltip
                    className="tooltip"
                    multiline={true}
                    id="allowResidentsToBook"
                    place="top"
                    effect="solid"
                  >
                    {t('reservations:amenityForms.details.allowResidentsToBookTooltip')}
                  </ReactTooltip>
                </div>
              </div>
              <div>
                <ToggleField
                  name="hidden"
                  label={
                    !values.hidden
                      ? t('reservations:amenityForms.yes')
                      : t('reservations:amenityForms.no')
                  }
                  value={!values.hidden}
                  onChange={() => setFieldValue('hidden', !values.hidden)}
                  labelOnRight
                />
              </div>
            </div>
            <div className="form-step__divider" />
            {isAmenitiesAcrAvailable && (
              <>
                <div className="form-step__row form-step__row--between">
                  <div>
                    <div className="form-step__label">
                      {t('reservations:singleReservationAmenityForm.amenityKeylessAccess')}
                      <Info size={16} data-tip data-for="saltoSpaceDoors" />
                      <ReactTooltip
                        className="tooltip"
                        multiline={true}
                        id="saltoSpaceDoors"
                        place="top"
                        effect="solid"
                      >
                        {t('reservations:amenityForms.details.amenityKeylessAccessTooltip')}
                      </ReactTooltip>
                    </div>
                  </div>
                  <div>
                    <ToggleField
                      name="saltoSpaceAccessAvailable"
                      label={
                        values.saltoSpaceAccessAvailable
                          ? t('reservations:amenityForms.yes')
                          : t('reservations:amenityForms.no')
                      }
                      value={values.saltoSpaceAccessAvailable}
                      onChange={() =>
                        setFieldValue(
                          'saltoSpaceAccessAvailable',
                          !values.saltoSpaceAccessAvailable,
                        )
                      }
                      labelOnRight
                    />
                  </div>
                </div>
                {values.saltoSpaceAccessAvailable && !!saltoSpaceDoors.length && (
                  <div className="columns">
                    <div className="column">
                      <Select
                        styles={saltoSpaceDropdownStyles()}
                        classNamePrefix="dropdown-button"
                        options={saltoSpaceDoors}
                        getOptionLabel={(option) => option.name.toString()}
                        getOptionValue={(option) => option.saltoId}
                        value={values.saltoSpaceDoors}
                        onChange={(selectedOptions) =>
                          setFieldValue('saltoSpaceDoors', selectedOptions)
                        }
                        placeholder={t(
                          'reservations:singleReservationAmenityForm.selectDoorPlaceholder',
                        )}
                        isClearable
                        isMulti
                        closeMenuOnSelect={false}
                      />
                    </div>
                  </div>
                )}

                <div className="form-step__divider" />
              </>
            )}
          </div>
        </div>
      </div>
      {isVideoModalVisible && (
        <VideoPreviewModal
          handleCloseVideo={handleCloseVideo}
          videoUrl="https://redvike-chefs.s3.us-east-1.amazonaws.com/amenityAgreement09102025.mp4"
        />
      )}
    </>
  )
}

AmenityDetails.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setIsMultiDayBooking: PropTypes.func,
  isEdit: PropTypes.bool,
}

export default AmenityDetails
